import { useEffect, useRef } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOrpc, type OrpcClient } from '@/hooks/useClient'

/** CRM data (PD-1993) from the /v2/crm endpoints (docs/CRM.md). */

export type LeadListInput = Parameters<OrpcClient['crmLeadList']>[0]
export type LeadListResult = Awaited<ReturnType<OrpcClient['crmLeadList']>>
export type Lead = LeadListResult['data'][number]
export type LeadTouch = Lead['original']
export type LeadFilter = Pick<
  LeadListInput,
  'search' | 'status' | 'source' | 'state' | 'postalCode'
>
/** How far a lead got, as the report counts it (registered; booked or seen). */
export type LeadMilestone = NonNullable<LeadListInput['milestone']>
export type LeadReport = Awaited<ReturnType<OrpcClient['crmLeadReport']>>
/** How many leads, and how many of them registered and booked or were seen. */
export type LeadFunnel = LeadReport['sources'][number]
export type Campaign = Awaited<ReturnType<OrpcClient['crmCampaignGet']>>
export type CampaignFields = Parameters<OrpcClient['crmCampaignCreate']>[0]
export type CohortResult = Awaited<ReturnType<OrpcClient['crmCohortList']>>
export type CohortMember = CohortResult['data'][number]
/** One message to a member on one channel; a `both` campaign gives each member two. */
export type Delivery = CohortMember['deliveries'][number]
export type LeadCampaignsResult = Awaited<ReturnType<OrpcClient['crmLeadCampaigns']>>
export type LeadCampaign = LeadCampaignsResult['data'][number]
/** Population Health's cohorts, group → condition → measure, with today's counts. */
export type PopulationCohortTree = Awaited<ReturnType<OrpcClient['crmPopulationCohorts']>>
export type PopulationCohortRef = Parameters<
  OrpcClient['crmPopulationCohortCount']
>[0]['cohorts'][number]

export const LEAD_PAGE_SIZE = 25
export const CAMPAIGN_PAGE_SIZE = 50
export const COHORT_PAGE_SIZE = 25
export const LEAD_CAMPAIGNS_PAGE_SIZE = 20

const page = (pageNumber: number, size: number) => {
  const offset = (pageNumber - 1) * size
  return { offset, count: size, limit: offset + size }
}

/** One page of the project's leads; the previous page stays on screen while the next loads. */
export function useCrmLeads(input: LeadListInput) {
  const orpc = useOrpc()
  return useQuery({
    ...orpc.crmLeadList.queryOptions({ input }),
    placeholderData: keepPreviousData,
  })
}

/** How many leads match a filter — the count "Add matching leads" will add. */
export function useMatchingLeadCount(filter: LeadFilter) {
  const orpc = useOrpc()
  return useQuery({
    ...orpc.crmLeadList.queryOptions({
      input: { ...filter, page: { offset: 0, count: 0, limit: 0 } },
    }),
    select: (result) => result.page.total,
    placeholderData: keepPreviousData,
  })
}

/** Up to eight leads matching a search, for a picker's suggestions; idle under two characters. */
export function useLeadSuggestions(search: string) {
  const orpc = useOrpc()
  return useQuery({
    ...orpc.crmLeadList.queryOptions({
      input: { search, page: { offset: 0, count: 8, limit: 8 } },
    }),
    enabled: search.length >= 2,
    placeholderData: keepPreviousData,
  })
}

/** The report: leads by source, by ad campaign, and SMS/email results. */
export function useCrmLeadReport() {
  const orpc = useOrpc()
  return useQuery(orpc.crmLeadReport.queryOptions({ input: {} }))
}

/**
 * One report campaign's leads by status: an ad campaign's, or an SMS/email campaign's reached.
 * `delivered` — an SMS/email campaign's messages out so far — is part of the cache key, so the
 * counts reload as more go out while it is still sending, the last batch included.
 */
export function useLeadStatusBreakdown(
  input: Parameters<OrpcClient['crmLeadStatusBreakdown']>[0],
  delivered?: number,
) {
  const orpc = useOrpc()
  const options = orpc.crmLeadStatusBreakdown.queryOptions({ input })
  return useQuery({
    ...options,
    queryKey: [...options.queryKey, delivered],
    // The last counts stay up while the next load, rather than a spinner each batch.
    placeholderData: keepPreviousData,
  })
}

/** The latest campaigns a lead is in, with what happened to their message. */
export function useLeadCampaigns(leadId: number) {
  const orpc = useOrpc()
  return useQuery(
    orpc.crmLeadCampaigns.queryOptions({
      input: { leadId, page: page(1, LEAD_CAMPAIGNS_PAGE_SIZE) },
    }),
  )
}

/** Leads, campaigns and cohorts all change together; refetch whatever is on screen. */
function useInvalidateCrm() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  return () =>
    Promise.all(
      [
        orpc.crmLeadList.key(),
        orpc.crmLeadReport.key(),
        orpc.crmCampaignList.key(),
        orpc.crmCampaignGet.key(),
        orpc.crmCohortList.key(),
        orpc.crmLeadCampaigns.key(),
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    )
}

export function useSetLeadStatus() {
  const orpc = useOrpc()
  const invalidate = useInvalidateCrm()
  return useMutation(orpc.crmLeadSetStatus.mutationOptions({ onSuccess: invalidate }))
}

export function useSetLeadNotes() {
  const orpc = useOrpc()
  const invalidate = useInvalidateCrm()
  return useMutation(orpc.crmLeadSetNotes.mutationOptions({ onSuccess: invalidate }))
}

/** New lead: staff enter a lead by hand. */
export function useCreateLead() {
  const orpc = useOrpc()
  const invalidate = useInvalidateCrm()
  return useMutation(orpc.crmLeadCreate.mutationOptions({ onSuccess: invalidate }))
}

export function useImportPatients() {
  const orpc = useOrpc()
  const invalidate = useInvalidateCrm()
  return useMutation(orpc.crmLeadImportPatients.mutationOptions({ onSuccess: invalidate }))
}

/** Every 2 s while any of these campaigns still has messages to send; sending runs in the background. */
export const refetchWhileSending = (counts: Campaign['counts'][]) =>
  counts.some((c) => c.pending + c.sending > 0) ? 2000 : false

/** A page of campaigns, those named like `search` if given, refreshed while any is still sending. */
export function useCrmCampaigns(pageNumber: number, search?: string) {
  const orpc = useOrpc()
  return useQuery({
    ...orpc.crmCampaignList.queryOptions({
      input: { page: page(pageNumber, CAMPAIGN_PAGE_SIZE), search },
    }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      refetchWhileSending(query.state.data?.data.map((campaign) => campaign.counts) ?? []),
  })
}

/** A campaign, refreshed while it is still sending. */
export function useCrmCampaign(campaignId: number) {
  const orpc = useOrpc()
  return useQuery({
    ...orpc.crmCampaignGet.queryOptions({ input: { campaignId } }),
    refetchInterval: (query) => {
      const counts = query.state.data?.counts
      return refetchWhileSending(counts ? [counts] : [])
    },
  })
}

export function useCohort(campaignId: number, pageNumber: number, sending: boolean) {
  const orpc = useOrpc()
  const query = useQuery({
    ...orpc.crmCohortList.queryOptions({
      input: { campaignId, page: page(pageNumber, COHORT_PAGE_SIZE) },
    }),
    placeholderData: keepPreviousData,
    refetchInterval: sending ? 2000 : false,
  })
  // The campaign's counts can report it done after this list's last poll read a message still
  // sending: read the recipients once more when sending stops, so none is left showing "Sending".
  const { refetch } = query
  const wasSending = useRef(sending)
  useEffect(() => {
    if (wasSending.current && !sending) void refetch()
    wasSending.current = sending
  }, [sending, refetch])
  return query
}

export function useCreateCampaign() {
  const orpc = useOrpc()
  const invalidate = useInvalidateCrm()
  return useMutation(orpc.crmCampaignCreate.mutationOptions({ onSuccess: invalidate }))
}

export function useUpdateCampaign() {
  const orpc = useOrpc()
  const invalidate = useInvalidateCrm()
  return useMutation(orpc.crmCampaignUpdate.mutationOptions({ onSuccess: invalidate }))
}

export function useLaunchCampaign() {
  const orpc = useOrpc()
  const invalidate = useInvalidateCrm()
  return useMutation(orpc.crmCampaignLaunch.mutationOptions({ onSuccess: invalidate }))
}

export function useAddToCohort() {
  const orpc = useOrpc()
  const invalidate = useInvalidateCrm()
  return useMutation(orpc.crmCohortAdd.mutationOptions({ onSuccess: invalidate }))
}

export function useRemoveFromCohort() {
  const orpc = useOrpc()
  const invalidate = useInvalidateCrm()
  return useMutation(orpc.crmCohortRemove.mutationOptions({ onSuccess: invalidate }))
}

/** Removes every lead from a draft's cohort. */
export function useClearCohort() {
  const orpc = useOrpc()
  const invalidate = useInvalidateCrm()
  return useMutation(orpc.crmCohortClear.mutationOptions({ onSuccess: invalidate }))
}

/** Population Health's cohorts a campaign can draw from; read only while `enabled`. */
export function usePopulationCohorts(enabled: boolean) {
  const orpc = useOrpc()
  return useQuery({ ...orpc.crmPopulationCohorts.queryOptions(), enabled })
}

/** How many patients a cohort selection holds, each once; idle while nothing is picked. */
export function usePopulationCohortCount(cohorts: PopulationCohortRef[]) {
  const orpc = useOrpc()
  return useQuery({
    ...orpc.crmPopulationCohortCount.queryOptions({ input: { cohorts } }),
    enabled: cohorts.length > 0,
    placeholderData: keepPreviousData,
  })
}

/** Adds a cohort selection's patients to a draft, making leads of those not in the CRM. */
export function useAddPopulationCohorts() {
  const orpc = useOrpc()
  const invalidate = useInvalidateCrm()
  return useMutation(orpc.crmCohortAddPopulation.mutationOptions({ onSuccess: invalidate }))
}
